// tracking-utils.js

const API_URL = "https://www.api.kenankorn.com"

// Constants for event types
export const EventTypes = {
    PAGE_VIEW: 'PAGE_VIEW',
    SCROLL: 'SCROLL',
    RESIZE: 'RESIZE',
    VIEW_IMAGE: 'VIEW_IMAGE',
    VIEW_COLLECTION: 'VIEW_COLLECTION',
    PAGE_CHANGE: 'PAGE_CHANGE',
    SESSION_START: 'SESSION_START',
    PAGE_EXIT: 'PAGE_EXIT',
    CLICK: 'CLICK',
    SCROLL_MILESTONE: 'SCROLL_MILESTONE'
  };

  // How far down the page the viewport bottom currently sits, 0–100.
  export const getScrollDepthPercent = () => {
    const height = document.documentElement.scrollHeight;
    if (!height) return 0;
    return Math.min(100, Math.round(((window.scrollY + window.innerHeight) / height) * 100));
  };

  const deviceType = () =>
    /Mobile|iP(hone|od|ad)|Android|BlackBerry|IEMobile/.test(navigator.userAgent) ? 'mobile' : 'desktop';
  
  // Helper to format interaction details
  const formatInteractionDetails = (type, details = {}) => {
    const baseDetails = {
      timestamp: new Date().toISOString(),
      path: window.location.pathname,
      url: window.location.href,
      userAgent: navigator.userAgent,
      viewport: {
        width: window.innerWidth,
        height: window.innerHeight
      },
      deviceInfo: {
        type: /Mobile|iP(hone|od|ad)|Android|BlackBerry|IEMobile/.test(navigator.userAgent) ? 'mobile' : 'desktop',
        platform: navigator.platform,
        vendor: navigator.vendor
      },
      performance: {
        timeOnPage: Math.floor((Date.now() - performance.timing.navigationStart) / 1000),
        loadTime: window.performance.timing.loadEventEnd - window.performance.timing.navigationStart,
        renderTime: window.performance.timing.domComplete - window.performance.timing.domLoading
      }
    };
  
    // Add specific details based on event type
    switch (type) {
      case EventTypes.SCROLL:
        return {
          ...baseDetails,
          scrollDepth: `${((window.pageYOffset + window.innerHeight) / document.documentElement.scrollHeight * 100).toFixed(2)}%`,
          scrollPosition: window.pageYOffset,
          pageHeight: document.documentElement.scrollHeight,
          ...details
        };
  
      // The fallbacks below deliberately sit *after* the spread. JSON.stringify
      // drops undefined values outright, so a field the caller passes as
      // undefined would clobber its default and then leave the wire entirely —
      // the backend reads an absent key, gets undefined, and Firestore rejects
      // the write ("Unsupported field value: undefined"). Each fallback already
      // folds in details.x, so caller-supplied values still win.
      case EventTypes.VIEW_IMAGE:
        return {
          ...baseDetails,
          ...details,
          imageId: details.imageId || 'unknown',
          imageName: details.imageName || 'unknown',
          imageLocation: details.imageLocation || { city: 'unknown', country: 'unknown' },
          collectionName: details.collectionName || 'unknown',
          // Dwell time isn't known at open — send a real 0 rather than nothing.
          duration: Number(details.duration) || 0,
          viewDuration: Number(details.duration) || 0
        };

      case EventTypes.VIEW_COLLECTION:
        return {
          ...baseDetails,
          ...details,
          collectionName: details.collectionName || 'unknown',
          imagesCount: details.imagesCount || 0,
          filterType: details.filterType || 'default'
        };

      case EventTypes.PAGE_CHANGE:
        return {
          ...baseDetails,
          ...details,
          fromPage: details.fromPage || 1,
          toPage: details.toPage || 1,
          direction: details.direction || 'next',
          totalItems: details.totalImages || 0
        };
  
      default:
        return { ...baseDetails, ...details };
    }
  };
  
  // Main tracking function
  export const trackInteraction = async (type, rawDetails = {}) => {
    try {
      const userId = localStorage.getItem('userId');
      const accessToken = localStorage.getItem('accessToken');
  
      if (!userId || !accessToken) {
        console.warn('Missing userId or accessToken for tracking');
        return;
      }
  
      // Format the interaction details
      const details = formatInteractionDetails(type, rawDetails);
  
      console.log(`Tracking interaction: ${type}`, details);
  
      // Top-level fields mirror what the backend persists into interaction.metadata:
      // {type, details, path, screenSize, deviceType, timeSpent, scrollDepth}.
      const response = await fetch(`${API_URL}/track/interaction`, {
        method: 'POST',
        keepalive: true, // survives page unload — exit flushes rely on this
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
          'X-User-ID': userId
        },
        body: JSON.stringify({
          type,
          details,
          // Mirrored at the top level too: the image_views writer reads the
          // duration from the body on some routes and from details on others,
          // and either one arriving absent is what Firestore rejects.
          ...(typeof details.duration === 'number' ? { duration: details.duration } : {}),
          userId,
          sessionId: localStorage.getItem('sessionId'),
          path: window.location.pathname,
          screenSize: `${window.innerWidth}x${window.innerHeight}`,
          deviceType: deviceType(),
          timeSpent: Math.floor((Date.now() - performance.timing.navigationStart) / 1000),
          scrollDepth: typeof rawDetails.scrollDepth === 'number' ? rawDetails.scrollDepth : getScrollDepthPercent()
        })
      });
  
      if (!response.ok) {
        throw new Error(`Failed to track interaction: ${response.status}`);
      }
  
      console.log(`Successfully tracked interaction: ${type}`);
    } catch (error) {
      console.error('Error tracking interaction:', error);
    }
  };

  // Capture-phase click tracking: sees every click before React handlers,
  // attributes clicks on gallery frames via data-image-id/-name on <figure>.
  // Returns a cleanup function.
  export const initClickTracking = () => {
    const onClick = (e) => {
      const el = e.target instanceof Element ? e.target : null;
      if (!el) return;
      const figure = el.closest('[data-image-id]');
      const actionable = el.closest('a, button, [role="button"]');
      trackInteraction(EventTypes.CLICK, {
        x: e.clientX,
        y: e.clientY,
        target: {
          tag: el.tagName.toLowerCase(),
          id: el.id || null,
          className: typeof el.className === 'string' ? el.className.slice(0, 120) : null,
          text: (actionable?.textContent || '').trim().slice(0, 80) || null,
          href: actionable?.getAttribute?.('href') || null
        },
        imageId: figure?.dataset.imageId || null,
        imageName: figure?.dataset.imageName || null
      });
    };
    document.addEventListener('click', onClick, true);
    return () => document.removeEventListener('click', onClick, true);
  };

  // Max-scroll-depth milestones (25/50/75/100, fired once each as crossed)
  // plus a PAGE_EXIT flush of the session max when the page is hidden/unloaded.
  // Returns a cleanup function.
  export const initScrollDepthTracking = () => {
    const MILESTONES = [25, 50, 75, 100];
    const fired = new Set();
    let maxDepth = 0;
    let flushedAt = -1; // last maxDepth we flushed — re-flush only if it grew

    const onScroll = () => {
      const depth = getScrollDepthPercent();
      if (depth > maxDepth) maxDepth = depth;
      MILESTONES.forEach((m) => {
        if (depth >= m && !fired.has(m)) {
          fired.add(m);
          trackInteraction(EventTypes.SCROLL_MILESTONE, { milestone: m, scrollDepth: depth });
        }
      });
    };

    const flush = () => {
      if (maxDepth <= flushedAt) return;
      flushedAt = maxDepth;
      trackInteraction(EventTypes.PAGE_EXIT, {
        scrollDepth: maxDepth,
        maxScrollDepth: maxDepth,
        milestones: [...fired],
        timeSpent: Math.floor((Date.now() - performance.timing.navigationStart) / 1000)
      });
    };

    const onVisibility = () => { if (document.visibilityState === 'hidden') flush(); };

    onScroll(); // record the landing depth (short pages can start at 100)
    window.addEventListener('scroll', onScroll, { passive: true });
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pagehide', flush);
    return () => {
      window.removeEventListener('scroll', onScroll);
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pagehide', flush);
    };
  };